<template>
  <ReuseableModal :modelValue="isOpen" @update:modelValue="$emit('update:isOpen', $event)" title="Report a Bug"
    :scrollable="true" :loading="loading" submitButtonText="Submit" closeButtonText="Close" :fullOverlay="fullOverlay"
    @submit="submitBugReport" @close="resetForm">
    <div class="form-group margin-base-y" style="position: relative;">
      <label for="email" class="control-label">Email <span class="required-asterisk">*</span></label>
      <div class="input-container">
        <input style="border: 1px solid #DDDDDD !important;" type="email" id="email"
          class="form-control bug-input-field" placeholder="Enter your email" v-model="form.email"
          :disabled="isUserLoggedIn" />
      </div>
      <div :style="isUserLoggedIn ? 'font-size: 12px; margin-top: -5px;' : 'font-size: 12px; margin-top: 5px;'"
        class="text-danger" v-if="error.email">
        {{ error.email }}
      </div>
    </div>

    <div class="form-group">
      <label for="description">How to reproduce the bug? <span class="required-asterisk">*</span></label>
      <textarea style="border: 1px solid #DDDDDD !important;" id="description" class="form-control bug-text-area"
        placeholder="Type your description..." v-model="form.description" required=""></textarea>
      <div :style="isUserLoggedIn ? 'font-size: 12px; margin-top: -5px;' : 'font-size: 12px; margin-top: 5px;'"
        class="text-danger" v-if="error.description">{{ error.description
        }}</div>
    </div>

    <div class="">
      <label for="priority">Priority</label>
      <CustomDropdown name="priority" id="priority" label="" :options="priorities" v-model="form.priority"
        placeholder="Select your priority" :show-error="false" @change="handlePriorityChange" />
    </div>

    <div class="form-group" style="margin-top: -10px;">
      <label for="priority">Screenshot</label>
      <div class="file-upload-area" v-if="!form.screenshot" @click="triggerFileInput" @dragover.prevent
        @drop.prevent="handleFile">
        <input type="file" ref="fileInput" style="display:none" @change="handleFile"
          accept="image/*" />
        <div class="upload-icon"><i class="fas fa-upload"></i></div>
        <div class="upload-text">
          Click to browse or drag and drop
          <div class="upload-format">Supported formats: PNG, JPEG, JPG</div>
        </div>
      </div>

      <div v-else class="file-preview">
        <div class="file-info">
          <img v-if="isImageFile" :src="form.screenshot" class="preview-image" />
          <i v-else-if="isPdfFile" class="fas fa-file-pdf fa-3x"></i>
          <i v-else class="fas fa-file-word fa-3x"></i>
        </div>
        <div class="action-button-container">
          <button type="button" @click="removeFile"><i class="fas fa-trash-alt"></i></button>
        </div>
      </div>
      <div style="font-size: 12px; margin-top: 3px;" class="text-danger" v-if="error.screenshot">{{ error.screenshot }}
      </div>
    </div>
  </ReuseableModal>
</template>

<script>
import ReuseableModal from "./ReuseableModal.vue";
import CustomDropdown from "./CustomDropdown.vue";
import { createBugReportPayload, logBugReport } from "../../utils/bugReport";

const DESCRIPTION_MIN_LENGTH = 10;
const DESCRIPTION_MAX_LENGTH = 500;

export default {
  components: { ReuseableModal, CustomDropdown },
  props: {
    isOpen: { type: Boolean, default: false },
    fullOverlay: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      form: {
        email: "",
        description: "",
        priority: "Normal",
        screenshot: null
      },
      priorities: ["Urgent", "High", "Normal", "Low"],
      isImageFile: false,
      isPdfFile: false,
      error: {
        email: "",
        description: "",
        screenshot: "",
      }
    };
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (user && user.email) {
          this.form.email = user.email;
        }
      }
    },
    "form.email"(val) {
      if (val) this.error.email = "";
    },
    "form.description"(val) {
      if (val) this.error.description = "";
    },
    'form.screenshot'() {
      this.error.screenshot = '';
    },
    "isOpen"(newVal) {
      if (!newVal) {
        this.resetForm();
        this.error = { email: '', description: '', screenshot: '' };
      }
    }
  },
  computed: {
    user() {
      return this.$store.state.auth.user || null;
    },
    isUserLoggedIn() {
      return !!(this.user && this.user.email);
    }
  },
  methods: {
    isValidEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },

    validateScreenshot(file) {
      if (!file) return true;

      const MAX_SIZE_MB = 5;
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      if (typeof file === 'string') {
        if (!file.startsWith('data:image/')) {
          return 'Only JPG, JPEG, PNG images are allowed.';
        }

        const sizeInBytes = (file.length * 3) / 4;
        if (sizeInBytes > MAX_SIZE_MB * 1024 * 1024) {
          return 'Image size must be less than 5MB.';
        }

        return true;
      }

      if (!allowedTypes.includes(file.type)) {
        return 'Only JPG, JPEG, PNG images are allowed.';
      }

      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        return 'Image size must be less than 5MB.';
      }

      return true;
    },
    handlePriorityChange(selectedPriority) {
      this.form.priority = selectedPriority;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.screenshot = e.target.result;
          this.isImageFile = true;
          this.isPdfFile = false;
        };
        reader.readAsDataURL(file);
      } else if (file.type === "application/pdf") {
        this.form.screenshot = file;
        this.isImageFile = false;
        this.isPdfFile = true;
      } else {
        this.form.screenshot = file;
        this.isImageFile = false;
        this.isPdfFile = false;
      }
    },
    removeFile() {
      this.form.screenshot = null;
      this.isImageFile = false;
      this.isPdfFile = false;
      this.$refs.fileInput.value = null;
    },
    openFileInNewTab(file) {
      if (!file) return;
      const url = typeof file === "string" ? file : URL.createObjectURL(file);
      window.open(url, "_blank");
    },
    async submitBugReport() {
      this.error = { email: '', description: '', screenshot: '' };

      if (!this.form.email) {
        this.error.email = "Email is required.";
        return;
      }

      if (!this.isValidEmail(this.form.email)) {
        this.error.email = "Please enter a valid email address.";
        return;
      }

      if (!this.form.description) {
        this.error.description = "Description is required.";
        return;
      }
      const descLength = this.form.description.trim().length;
      if (descLength < DESCRIPTION_MIN_LENGTH) {
        this.error.description = `Description must be at least ${DESCRIPTION_MIN_LENGTH} characters.`;
        return;
      }
      if (descLength > DESCRIPTION_MAX_LENGTH) {
        this.error.description = `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`;
        return;
      }

      const screenshotValidation = this.validateScreenshot(this.form.screenshot);
      if (screenshotValidation !== true) {
        this.error.screenshot = screenshotValidation;
        return;
      }

      this.loading = true;

      try {
        const payload = createBugReportPayload(this.form);
        const firebaseConfig = localStorage.getItem('firebaseConfig') || null;
        await logBugReport(payload, firebaseConfig);
        swal('Success', 'Bug report submitted successfully', 'success');
        this.resetForm();
      } catch (err) {
        console.error(err);
        swal('Failed', 'Bug report submission failed', 'error');
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form.description = "";
      this.form.priority = "Normal";
      this.form.screenshot = null;

      if (this.isUserLoggedIn) {
        this.form.email = this.user.email;
      } else {
        this.form.email = "";
      }

      this.isImageFile = false;
      this.isPdfFile = false;

      this.$emit("update:isOpen", false);
    }
  }
};
</script>

<style scoped>
.file-upload-area {
  border: 2px dashed #ced4da;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out;
  background-color: #f8f9fa;
  position: relative;
  width: 100%;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.required-asterisk {
  color: red;
}

.file-upload-area:hover {
  border-color: #4CAF50;
}

.bug-input-field {
  font-size: 14px;
  padding: 12px 10px !important;
  width: 28.3rem !important;
}

.bug-text-area {
  padding-left: 10px !important;
  font-size: 14px;
  border-radius: 5px !important;
  height: 100px;
}

.file-preview {
  border: 2px dashed #ced4da;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 28.3rem;
}

.preview-image {
  max-width: 150px;
  max-height: 100px;
  object-fit: contain;
  border-radius: 4px;
}

.action-button-container button {
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 5px;
}

@media screen and (max-width: 800px) {
  .file-preview {
    width: 100%;
  }

  .bug-input-field {
    width: 100% !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/UIComponents/BugReportModal.vue